import Info from '@/assets/images/Info.svg';
import ListHeader, { SessionFiltersState } from '@/components/common/Header/ListHeader';
import SessionCard from '@/components/common/SessionCard/SessionCard';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { showToast } from '@/components/ui/sonner';
import useCancelSession from '@/hooks/useCancelSession';
import { getRoutes } from '@/routes/routePaths';
import { Role, Session, SessionStatus } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import FirstSessionCard from './components/FirstSessionCard';
import ScheduleSession from './components/ScheduleSession';
import { GET_ALL_SESSIONS, GET_ALL_SESSIONS_COUNT, GET_SESSION_BALANCE } from './GraphQl/queries';

const Sessions = () => {
  const { userDetails } = authState();
  const [filters, setFilters] = useState({
    sessionStartDate: '',
    sessionEndDate: '',
    programId: '',
  });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<{ id: string, programId: string, sportId?: string } | null>(null);
  const [sessionData, setSessionData] = useState<Session>();
  const [startingSoon, setStartingSoon] = useState<Session[]>([]);
  const [upcoming, setUpcoming] = useState<Session[]>([]);
  const [completed, setCompleted] = useState<Session[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [filterCount, setFilterCount] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState<string | undefined>(undefined);

  const { data, loading, refetch, fetchMore } = useQuery(GET_ALL_SESSIONS, {
    variables: {
      limit: 30,
      offset: pageNo * 30,
      search: search,
      sort: {
        sessionStartTime: -1,
      },
      filter: {
        ...(userDetails?.role === Role.Trainee
          ? { traineeId: userDetails?._id }
          : { trainerId: userDetails?._id }),
        sessionStatus: {
          $in: [
            'Scheduled',
            'Completed',
            'Cancelled',
            'Open',
            SessionStatus.Noshow,
            SessionStatus.Reassign,
          ],
        },
        ...(filters.sessionStartDate && { sessionStartTime: { $gte: filters.sessionStartDate } }),
        ...(filters.sessionEndDate && { sessionEndTime: { $lte: filters.sessionEndDate } }),
        ...(filters.programId && { programId: filters.programId }),
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      sortSessions(res.getAllSession as Session[]);
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: pendingFeedback } = useQuery(GET_ALL_SESSIONS_COUNT, {
    variables: {
      filter: {
        traineeId: userDetails?._id,
        sessionStatus: 'Completed',
        trainerExpertiseRating: null,
      },
    },
  });

  const { data: sessionBalance } = useQuery(GET_SESSION_BALANCE, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (data?.getAllSession) {
      sortSessions(data.getAllSession as Session[]);
      setTotalCount(data.getAllSessionCount);
    }
  }, [data]);

  const sortSessions = (sessions: Session[]) => {
    const currentTime = new Date();
    const soonSessions: Session[] = [];
    const upcomingSessions: Session[] = [];
    const completedSessions: Session[] = [];

    sessions.forEach((session) => {
      if (session) {
        const startTime = new Date(session.sessionStartTime);
        const endTime = new Date(session.sessionEndTime);
        const sessionStatus = session.sessionStatus;

        if (sessionStatus === 'Cancelled' || sessionStatus === 'Completed') {
          completedSessions.push(session);
        } else if (startTime <= new Date(currentTime.getTime()) && session?.trainerId === null) {
          completedSessions.push(session);
        } else if (
          sessionStatus === SessionStatus.Noshow &&
          startTime <= currentTime &&
          endTime > currentTime &&
          session.trainerId
        ) {
          soonSessions.push(session);
        } else if (
          (sessionStatus === 'Scheduled' ||
            sessionStatus === 'Open' ||
            sessionStatus === SessionStatus.Noshow) &&
          startTime <= currentTime &&
          endTime > currentTime &&
          session.trainerId
        ) {
          soonSessions.push(session);
        } else if (
          (sessionStatus === 'Scheduled' || sessionStatus === 'Open') &&
          startTime > currentTime &&
          startTime <= new Date(currentTime.getTime() + 60 * 60000) &&
          session.trainerId
        ) {
          soonSessions.push(session);
        } else if (
          (sessionStatus === 'Scheduled' || sessionStatus === 'Open') &&
          startTime > new Date(currentTime.getTime() + 60 * 60000)
        ) {
          upcomingSessions.push(session);
        } else if (endTime <= currentTime) {
          completedSessions.push(session);
        } else if (
          (sessionStatus === 'Scheduled' || sessionStatus === 'Open') &&
          !session.trainerId
        ) {
          upcomingSessions.push(session);
        }
      }
    });

    soonSessions.sort(
      (a, b) => new Date(a.sessionStartTime).getTime() - new Date(b.sessionStartTime).getTime(),
    );
    upcomingSessions.sort(
      (a, b) => new Date(a.sessionStartTime).getTime() - new Date(b.sessionStartTime).getTime(),
    );
    completedSessions.sort(
      (a, b) => new Date(b.sessionEndTime).getTime() - new Date(a.sessionEndTime).getTime(),
    );

    // Update states with the newly sorted sessions
    setStartingSoon(soonSessions);
    setUpcoming(upcomingSessions);
    setCompleted(completedSessions);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompleted([]);
    setUpcoming([]);
    setStartingSoon([]);
    setPageNo(0);
    setSearch(e.target.value);
  };

  const getTotalBalance = () => {
    const fitPrimeBalance = sessionBalance?.getCurrentUser?.fitPrimeBalance || 0;
    const fitCurveBalance = sessionBalance?.getCurrentUser?.fitCurveBalance || 0;
    const fitVarsityBalance = sessionBalance?.getCurrentUser?.fitVarsityBalance || 0;
    const freeSessionBalance = sessionBalance?.getCurrentUser?.freeSessionBalance || 0;
    return fitPrimeBalance + fitCurveBalance + fitVarsityBalance + freeSessionBalance;
  };

  // Show only first 5 items initially - moved to ProgramSlider component

  const addButtonClick = (program?: string) => {
    const totalBalance = getTotalBalance();

    if (totalBalance > 0) {
      setSessionData(undefined);
      setSelectedProgram(program);
      setOpen(true);
    } else {
      showToast({
        message: 'Session balance is empty. Purchase a package to schedule.',
        variant: 'error',
      });
    }
  };

  const handleFiltersApplied = (filters: SessionFiltersState) => {
    setCompleted([]);
    setUpcoming([]);
    setStartingSoon([]);
    setPageNo(0);
    setFilters({
      programId: filters.category,
      sessionStartDate: filters.startDate?.toISOString() || '',
      sessionEndDate: filters.endDate?.toISOString() || '',
    });
  };

  const { handleCancelSession } = useCancelSession();

  const handleCancelClick = ({ id, programId, sportId }: { id: string, programId: string, sportId?: string }) => {
    setSelectedSession({ id, programId, sportId });
    setCancelDialog(true);
  };

  const handleEditClick = (session: Session) => {
    setSessionData(session);
    setOpen(true);
  };

  const sessionRefetch = () => {
    refetch({})
      .then((result) => {
        // If the refetch was successful, update the sessions
        if (result.data?.getAllSession) {
          sortSessions(result.data.getAllSession as Session[]);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Refetch failed:', error);
      });
  };
  const handleLoadMore = () => {
    fetchMore({
      variables: { offset: data?.getAllSession.length || 0, limit: 30 },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getAllSession) return previousResult;
        return {
          ...previousResult,
          getAllSession: [...previousResult.getAllSession, ...fetchMoreResult.getAllSession],
        };
      },
    });
  };

  if (userDetails?.role === Role.Trainee) {
    return (
      <div className='bg-[#00001C]'>
        <ListHeader
          userName={userDetails?.firstName + ' ' + userDetails?.lastName}
          showSearch
          searchPlaceholder='Search by ID'
          onSearchChange={handleSearch}
          showButton
          buttonLabel='Schedule New Session'
          onButtonClick={addButtonClick}
          onFiltersApplied={handleFiltersApplied}
          filterCount={filterCount}
          setFilterCount={setFilterCount}
        />
        {!loading &&
          filterCount === 0 &&
          !search &&
          (!data?.getAllSession || data.getAllSession.length === 0) &&
          (!completed || completed.length === 0) ? (
          <div className='h-full w-full flex justify-center items-center'>
            <FirstSessionCard setOpen={addButtonClick} />
          </div>
        ) : (
          <div
            id='sessions'
            className='flex flex-col container py-14 w-[1060px] max-w-[98%] gap-14'
          >
            <div id='soonSession'>
              <div className='flex items-center justify-between'>
                <span className='font-bold leading-6 '>Starting Soon</span>
                {pendingFeedback?.getAllSessionCount && pendingFeedback.getAllSessionCount > 0 ? (
                  <Link to={getRoutes.FeedbackPending}>
                    <div
                      className='flex items-center gap-1 px-2 py-1 rounded-full'
                      style={{ background: '#E9CC7C38' }}
                    >
                      <img src={Info} alt='info-icon' />
                      <h5 className='font-normal'>
                        {pendingFeedback?.getAllSessionCount}{' '}
                        {pendingFeedback?.getAllSessionCount > 1 ? 'Reviews' : 'Review'} Pending
                      </h5>
                    </div>
                  </Link>
                ) : (
                  ''
                )}
              </div>
              {loading ? (
                <div id='loading-skeletons' className='flex flex-col gap-4 mt-5'>
                  <Skeleton className='w-full h-20' />
                </div>
              ) : startingSoon.length > 0 ? (
                startingSoon.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    isSoon={true}
                  />
                ))
              ) : (
                <p className='pt-7 text-center'>
                  {filterCount > 0 || search ? 'No Results Found.' : 'No sessions starting soon.'}
                </p>
              )}
            </div>
            <div id='upcomingSession'>
              <span className='font-semibold leading-5 '>Upcoming Sessions</span>
              {loading ? (
                <div id='loading-skeletons' className='flex flex-col gap-4 mt-5'>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton key={index} className='w-full h-20' />
                  ))}
                </div>
              ) : upcoming.length > 0 ? (
                upcoming.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    status='Scheduled'
                  />
                ))
              ) : (
                <p className='pt-3 text-center'>
                  {filterCount > 0 || search ? 'No Results Found.' : 'No upcoming sessions.'}
                </p>
              )}
            </div>
            <div id='completedSession'>
              <span className='font-semibold leading-5 '>Completed/Canceled</span>
              {completed.length > 0 ? (
                completed.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    status={
                      session.sessionStatus === SessionStatus.Completed
                        ? 'Completed'
                        : session.sessionStatus === SessionStatus.Cancelled ||
                          session.sessionStatus === SessionStatus.Open
                          ? 'Canceled'
                          : 'No-Show'
                    }
                  />
                ))
              ) : (
                <p className='pt-3 text-center'>
                  {filterCount > 0 || search
                    ? 'No Results Found.'
                    : 'No completed or canceled sessions.'}
                </p>
              )}
              {totalCount &&
                totalCount > 10 &&
                completed.length + startingSoon.length + upcoming.length < totalCount ? (
                <div className='w-full p-5 text-center'>
                  <Button id='loadmore-sessions' onClick={handleLoadMore} loading={loading}>
                    Load More
                  </Button>
                </div>
              ) : (
                ''
              )}
              {loading && (
                <div id='loading-skeletons' className='flex flex-col gap-4'>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton key={index} className='w-full h-20' />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {open && (
          <ScheduleSession
            open={open}
            setOpen={setOpen}
            sessionData={sessionData}
            sessionUpdation={sessionRefetch}
            selectedProgram={selectedProgram}
            sessionBalance={data && data?.getAllSessionCount}
            freeSessionBalance={sessionBalance?.getCurrentUser?.freeSessionBalance ?? undefined}
            fitPrimeBalance={sessionBalance?.getCurrentUser?.fitPrimeBalance ?? undefined}
            fitCurveBalance={sessionBalance?.getCurrentUser?.fitCurveBalance ?? undefined}
            fitVarsityBalance={sessionBalance?.getCurrentUser?.fitVarsityBalance ?? undefined}
          />
        )}
        <CustomDialog
          open={cancelDialog}
          desc='Are you sure you want to cancel session?'
          onCancel={() => setCancelDialog(false)}
          title='Cancel Session'
          onConfirm={() => handleCancelSession(selectedSession?.id || '', selectedSession?.programId || '', selectedSession?.sportId || '')}
        />
      </div>
    );
  } else if (userDetails?.role === Role.Trainer || userDetails?.role === Role.Coach) {
    return (
      <div>
        <ListHeader
          userName={userDetails?.firstName + ' ' + userDetails?.lastName}
          showSearch
          searchPlaceholder='Search by ID'
          onSearchChange={handleSearch}
          showButton={false}
          onFiltersApplied={handleFiltersApplied}
          filterCount={filterCount}
          setFilterCount={setFilterCount}
        />
        {data?.getAllSession.length === 0 ? (
          <div className='mt-40 w-full flex justify-center items-center text-2xl'>
            You Have No Sessions.
          </div>
        ) : (
          <div
            id='sessions'
            className='flex flex-col container py-14 w-[1060px] max-w-[98%] gap-14'
          >
            <div id='soonSession'>
              <div className='flex items-center justify-between'>
                <span className='font-bold leading-6 '>Starting Soon</span>
                {pendingFeedback?.getAllSessionCount && pendingFeedback.getAllSessionCount > 0 ? (
                  <Link to={getRoutes.FeedbackPending}>
                    <div
                      className='flex items-center gap-1 px-2 py-1 rounded-full'
                      style={{ background: '#E9CC7C38' }}
                    >
                      <img src={Info} alt='info-icon' />
                      <h5 className='font-normal'>
                        {pendingFeedback?.getAllSessionCount}{' '}
                        {pendingFeedback?.getAllSessionCount > 1 ? 'Reviews' : 'Review'} Pending
                      </h5>
                    </div>
                  </Link>
                ) : (
                  ''
                )}
              </div>
              {loading ? (
                <div id='loading-skeletons' className='flex flex-col gap-4 mt-5'>
                  <Skeleton className='w-full h-20' />
                </div>
              ) : startingSoon.length > 0 ? (
                startingSoon.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    isSoon={true}
                  />
                ))
              ) : (
                <p className='pt-7 text-center'>
                  {filterCount > 0 || search ? 'No Results Found.' : 'No sessions starting soon.'}
                </p>
              )}
            </div>
            <div id='upcomingSession'>
              <span className='font-semibold leading-5 '>Upcoming Sessions</span>
              {loading ? (
                <div id='loading-skeletons' className='flex flex-col gap-4 mt-5'>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton key={index} className='w-full h-20' />
                  ))}
                </div>
              ) : upcoming.length > 0 ? (
                upcoming.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    status='Scheduled'
                  />
                ))
              ) : (
                <p className='pt-3 text-center'>
                  {filterCount > 0 || search ? 'No Results Found.' : 'No upcoming sessions.'}
                </p>
              )}
            </div>
            <div id='completedSession'>
              <span className='font-semibold leading-5 '>Completed/Canceled</span>
              {completed.length > 0 ? (
                completed.map((session) => (
                  <SessionCard
                    key={session._id}
                    session={session}
                    handleCancelClick={handleCancelClick}
                    handleEditClick={handleEditClick}
                    status={
                      session.sessionStatus === SessionStatus.Completed
                        ? 'Completed'
                        : session.sessionStatus === SessionStatus.Cancelled ||
                          session.sessionStatus === SessionStatus.Open
                          ? 'Canceled'
                          : 'No-Show'
                    }
                  />
                ))
              ) : (
                <p className='pt-3 text-center'>
                  {filterCount > 0 || search
                    ? 'No Results Found.'
                    : 'No completed or canceled sessions.'}
                </p>
              )}
              {totalCount &&
                totalCount > 10 &&
                completed.length + startingSoon.length + upcoming.length < totalCount ? (
                <div className='w-full p-5 text-center'>
                  <Button id='loadmore-sessions' onClick={handleLoadMore} loading={loading}>
                    Load More
                  </Button>
                </div>
              ) : (
                ''
              )}
              {loading && (
                <div id='loading-skeletons' className='flex flex-col gap-4'>
                  {[...Array(3)].map((_, index) => (
                    <Skeleton key={index} className='w-full h-20' />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modals */}
        {open && (
          <ScheduleSession
            open={open}
            setOpen={setOpen}
            sessionData={sessionData}
            sessionUpdation={sessionRefetch}
            selectedProgram={selectedProgram || 'fitvarsity'}
            sessionBalance={data && data?.getAllSessionCount}
            freeSessionBalance={sessionBalance?.getCurrentUser?.freeSessionBalance ?? undefined}
          />
        )}
      </div>
    );
  }
};

export default Sessions;
